import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import HeroSection from './components/HeroSection';
import WelcomeSection from './components/WelcomeSection';
import CateringIntroSection from './components/CateringIntroSection';

import DrinksMixologySection from './components/DrinksMixologySection';
import FeatureSlider from './components/FeatureSlider';
import OnlineOrderingSection from './components/OnlineOrderingSection';
import GallerySection from './components/GallerySection';
import SocialMediaSection from './components/SocialMediaSection';
import TestimonialsSection from './components/TestimonialsSection';
import FooterSection from './components/FooterSection';
import AnnouncementBar from './components/AnnouncementBar';

// Icons
import { MapPin } from 'lucide-react';

const HomeLanding = () => {
  const navigate = useNavigate();
  const [cartCount, setCartCount] = useState(0);
  const [user, setUser] = useState(null);
  const [isOrderMenuOpen, setIsOrderMenuOpen] = useState(false);
  const [showSpeechBubble, setShowSpeechBubble] = useState(false);
  const [showOrderPopup, setShowOrderPopup] = useState(false);

  // Mock user data - in real app this would come from auth context
  useEffect(() => {
    // Simulate checking for logged in user
    const mockUser = localStorage.getItem('currentUser');
    if (mockUser) {
      setUser(JSON.parse(mockUser));
    }

    // Simulate cart count from localStorage
    const mockCartCount = localStorage.getItem('cartCount');
    if (mockCartCount) {
      setCartCount(parseInt(mockCartCount, 10));
    }

    // Show speech bubble after component mounts
    const timer = setTimeout(() => {
      setShowSpeechBubble(true);
      // Hide after 5 seconds
      setTimeout(() => {
        setShowSpeechBubble(false);
      }, 5000);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const handleOrderNow = () => {
    navigate('/menu-catalog');
  };

  const handleViewDeal = (deal) => {
    if (deal === 'all') {
      navigate('/menu-catalog', { state: { showDeals: true } });
    } else {
      navigate('/menu-catalog', { state: { selectedDeal: deal } });
    }
  };

  const handleCartClick = () => {
    navigate('/shopping-cart');
  };

  const handleAccountClick = () => {
    if (user) {
      navigate('/user-account');
    } else {
      navigate('/login');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('currentUser');
    setUser(null);
    navigate('/home-landing');
  };

  const handleSearch = (query) => {
    navigate('/menu-catalog', { state: { searchQuery: query } });
  };

  const handleMakeReservation = () => {
    navigate('/table-reservation');
  };

  const handleCallClick = () => {
    setIsOrderMenuOpen(!isOrderMenuOpen);
  };

  const handleCallLocation = (location) => {
    if (location === 'durham') {
      window.location.href = 'tel:+19199730028';
    } else if (location === 'chapel-hill') {
      window.location.href = 'tel:+19849994537';
    }
    setIsOrderMenuOpen(false);
  };

  const handleOrderLocation = (location) => {
    if (location === 'durham') {
      window.open('https://online.skytab.com/d532ef127b6d5a39d4172cf8121d87bf', '_blank');
    } else if (location === 'chapel-hill') {
      window.open('https://online.skytab.com/5a689c513d0e59a5cb701768602d0256', '_blank');
    }
    setIsOrderMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-background overflow-x-hidden max-w-full">
      {/* Announcement Bar - Above Header */}
      <AnnouncementBar />
      {/* Header */}
      <Header
        cartCount={cartCount}
        user={user}
        onCartClick={handleCartClick}
        onAccountClick={handleAccountClick}
        onLogout={handleLogout}
        onSearch={handleSearch}
      />
      {/* Page Content */}
      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="pt-[104px] overflow-x-hidden"
      >
        {/* Hero Section */}
        <HeroSection 
          onOrderNow={handleOrderNow}
          onMakeReservation={handleMakeReservation}
        />

        {/* Welcome Section */}
        <WelcomeSection />

        {/* Daily Specials Slider */}
        <FeatureSlider />

        {/* Catering Intro Section */}
        <CateringIntroSection />

        {/* Drinks & Mixology Section */}
        <DrinksMixologySection />

        {/* Online Ordering Section */}
        <OnlineOrderingSection />

        {/* Gallery Section */}
        <GallerySection />

        {/* Social Media Section */}
        <SocialMediaSection />

        {/* Testimonials Section */}
        <TestimonialsSection />

        {/* Footer Section */}
        <FooterSection />
      </motion.main>
      {/* Speech Bubble - "Order Here!" */}
      {showSpeechBubble && (
        <div className="fixed bottom-6 right-6 sm:bottom-8 sm:right-8 z-[60] pointer-events-none">
          <div className="relative">
            {/* Position bubble further to the left of the button */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="absolute bottom-2 -left-[9rem] sm:bottom-2 sm:-left-[8rem] bg-primary text-primary-foreground px-4 py-2 rounded-lg shadow-lg whitespace-nowrap md:hidden"
            >
              <span className="font-semibold">Call Us</span>
              {/* Arrow pointing horizontally to the right (sideways at button) */}
              <div className="absolute top-1/2 -translate-y-1/2 -right-2 w-0 h-0 border-t-8 border-b-8 border-l-8 border-t-transparent border-b-transparent border-l-primary"></div>
            </motion.div>
          </div>
        </div>
      )}
      {/* Floating Action Buttons */}
      <div className="fixed bottom-6 right-6 sm:bottom-8 sm:right-8 z-50 flex flex-col-reverse gap-3 md:hidden">
        {/* Call Us Button */}
        <motion.button
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setIsOrderMenuOpen(!isOrderMenuOpen)}
          className="w-12 h-12 sm:w-14 sm:h-14 md:w-12 md:h-12 bg-primary text-primary-foreground rounded-full shadow-warm-lg hover:shadow-warm-xl transition-all duration-300 flex items-center justify-center relative"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
          </svg>
        </motion.button>
      </div>
      {/* Call Now Popup Menu */}
      {isOrderMenuOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOrderMenuOpen(false)}
            className="fixed inset-0 bg-transparent z-40"
          />
          
          {/* Mini Popup Menu */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            className="fixed bottom-[4.5rem] right-6 sm:bottom-[5.5rem] sm:right-8 md:bottom-[4.5rem] md:right-6 z-50 bg-card/95 backdrop-blur-lg border border-border rounded-2xl shadow-warm-xl overflow-hidden w-fit"
          >
            {/* Durham Option */}
            <a
              href="tel:+19199730028"
              onClick={() => setIsOrderMenuOpen(false)}
              className="block w-full px-4 py-2 text-left text-foreground hover:bg-primary/10 transition-all duration-200 border-b border-border/50"
            >
              <span className="font-body font-medium whitespace-nowrap">Durham</span>
            </a>

            {/* Chapel Hill Option */}
            <a
              href="tel:+19849994537"
              onClick={() => setIsOrderMenuOpen(false)}
              className="block w-full px-4 py-2 text-left text-foreground hover:bg-primary/10 transition-all duration-200"
            >
              <span className="font-body font-medium whitespace-nowrap">Chapel Hill</span>
            </a>
          </motion.div>
        </>
      )}
      {/* Order Now Popup Menu */}
      {showOrderPopup && (
        <div className="absolute bottom-[4.5rem] sm:bottom-[5rem] right-0 bg-white rounded-lg shadow-xl border border-gray-200 py-2 min-w-[200px] animate-fadeIn">
          <a
            href="https://www.toasttab.com/local/order/taqueria-el-toro-durham"
            target="_blank"
            rel="noopener noreferrer"
            className="block px-4 py-3 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <MapPin className="w-5 h-5 text-orange-500" />
              <div>
                <div className="font-semibold text-gray-900">Durham</div>
                <div className="text-sm text-gray-500">Order Online</div>
              </div>
            </div>
          </a>
          <div className="border-t border-gray-100"></div>
          <a
            href="https://www.toasttab.com/local/order/taqueria-el-toro-chapel-hill"
            target="_blank"
            rel="noopener noreferrer"
            className="block px-4 py-3 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <MapPin className="w-5 h-5 text-orange-500" />
              <div>
                <div className="font-semibold text-gray-900">Chapel Hill</div>
                <div className="text-sm text-gray-500">Order Online</div>
              </div>
            </div>
          </a>
        </div>
      )}
      {/* Mini Floating Popup Menu */}
      {isOrderMenuOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOrderMenuOpen(false)}
            className="fixed inset-0 bg-transparent z-40"
          />
          
          {/* Mini Popup Menu */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            className="fixed bottom-[4.5rem] right-6 sm:bottom-[5.5rem] sm:right-8 md:bottom-[4.5rem] md:right-6 z-50 bg-card/95 backdrop-blur-lg border border-border rounded-2xl shadow-warm-xl overflow-hidden w-fit"
          >
            {/* Durham Option */}
            <button
              onClick={() => handleOrderLocation('durham')}
              className="block w-full px-4 py-2 text-left text-foreground hover:bg-primary/10 transition-all duration-200 border-b border-border/50"
            >
              <span className="font-body font-medium whitespace-nowrap">Durham</span>
            </button>

            {/* Chapel Hill Option */}
            <button
              onClick={() => handleOrderLocation('chapel-hill')}
              className="block w-full px-4 py-2 text-left text-foreground hover:bg-primary/10 transition-all duration-200"
            >
              <span className="font-body font-medium whitespace-nowrap">Chapel Hill</span>
            </button>
          </motion.div>
        </>
      )}
    </div>
  );
};

export default HomeLanding;